/* eslint-disable max-lines */
/* eslint-disable complexity */
import type { Styles } from '@adornis/ass/style.js';
import { logger } from '@adornis/base/logging.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XButton } from '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/popover/x-tooltip.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { runTerminScheduler, subscribeTerminsBySlotIDs } from '@ils/evaluation-scheduler/api/EvaluationTermin.js';
import type { EvaluationTermin } from '@ils/evaluation-scheduler/db/EvaluationTermin.js';
import '@ils/osces/client/ils-osce-type.js';
import { getGroupsFromItemsAndTesterTuples } from '@ils/osces/db/osce-group.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { subscribeSlotsForEvaluation } from '@ils/ratings/api/evaluation.js';
import { subscribeScoresByExamDayAndCandidates } from '@ils/ratings/api/scores.js';
import type { Score, SlotScore } from '@ils/ratings/db/score.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { Specialization, UserRole } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import type { ILSThemeDefinition } from '@ils/theme/theme.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime, type Interval } from 'luxon';
import { type Observable, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { ExamDay } from '../../../db/examdays.js';
import { ILSUser } from '../../../db/ils-user.js';
import { getOrdersByExamDayAndTrainees, type Order } from '../../../db/order.js';
import { ILSEvaluationInspectDialog } from '../../components/ils-evaluation-inspect.js';
import '../../components/ils-loader.js';
import { isSidebarOpen } from '../../global-state.js';
import './ils-evaluation-overview-inspector.js';

// #region selectors n types
const slotSelector = {
  _class: 1,
  _id: 1,
  tester: 1,
  candidateID: 1,
  inspectors: 1,
  inspectionTime: 1,
  ratedAt: 1,
  isFallbackSlot: 1,
  fallbackSlotID: 1,
  isCandidateMissing: 1,
  ratings: {
    _id: 1,
    _class: 1,
    inspector: 1,
    committed: 1,
    updatedAt: 1,
    archived: 1,
  },
  answer: {
    _id: 1,
    _class: 1,
    slotID: 1,
    type: 1,
    text: 1,
    fileId: 1,
    onlyTraineeFileId: 1,
    filesPresent: 1,
    reportedError: {
      description: 1,
      errorType: 1,
      reportedAt: 1,
      reportedBy: 1,
    },
  },
  osceID: 1,
  osce: {
    _class: 1,
    _id: 1,
    titleTrainee: 1,
    ratingConfigName: 1,
    taskType: 1,
    osceType: 1,
    conversationType: 1,
    enableVideo: 1,
    instructionCategories: {
      _id: 1,
      _class: 1,
      instructions: {
        _id: 1,
        _class: 1,
      },
    },
  },
} as const satisfies BaseQLSelectionSet<Slot>;

const extendedExamDaySelector = {
  _id: 1,
  _class: 1,
  slotIDs: 1,
  osceTesterTuples: {
    osceID: 1,
    osceGroupID: 1,
  },
} as const satisfies BaseQLSelectionSet<ExamDay>;

const traineeSelector = {
  _id: 1,
  _class: 1,
  username: 1,
  ecommerce: { _class: 1, firstName: 1, lastName: 1, fullName: 1 },
  profile: {
    _class: 1,
    certificateEmailState: { _class: 1, certificate: 1, orderID: 1 },
    resultsEmailState: { _class: 1, resultID: 1, orderID: 1 },
  },
} as const satisfies BaseQLSelectionSet<ILSUser>;

const orderSelector = {
  _id: 1,
  status: 1,
  userReference: {
    userID: 1,
  },
} as const satisfies BaseQLSelectionSet<Order>;

const scoreSelector = {
  _id: 1,
  _class: 1,
  status: 1,
  examDayID: 1,
  candidateID: 1,
  slotScores: {
    _class: 1,
    linguistID: 1,
    doctorID: 1,
    slotID: 1,
    updatedAt: 1,
    score: 1,
    osceGroupID: 1,
    osceID: 1,
  },
} as const satisfies BaseQLSelectionSet<Score>;

const terminSelector = {
  _id: 1,
  _class: 1,
  slotIDs: 1,
  userIDs: 1,
  start: 1,
  end: 1,
} as const satisfies BaseQLSelectionSet<EvaluationTermin>;

const PRXController = <S extends BaseQLSelectionSet<ExamDay>>(
  ...args: ConstructorParameters<
    typeof RXController<Observable<NarrowedEntity<ExamDay, S> | null>, NarrowedEntity<ExamDay, S> | null>
  >
) => new RXController<Observable<NarrowedEntity<ExamDay, S> | null>, NarrowedEntity<ExamDay, S> | null>(...args);

const URXController = <S extends BaseQLSelectionSet<ILSUser>>(
  ...args: ConstructorParameters<
    typeof RXController<Observable<NarrowedEntity<ILSUser, S>[]>, NarrowedEntity<ILSUser, S>[]>
  >
) => new RXController<Observable<NarrowedEntity<ILSUser, S>[]>, NarrowedEntity<ILSUser, S>[]>(...args);

const ORXController = <S extends BaseQLSelectionSet<Order>>(
  ...args: ConstructorParameters<
    typeof RXController<Observable<NarrowedEntity<Order, S>[]>, NarrowedEntity<Order, S>[]>
  >
) => new RXController<Observable<NarrowedEntity<Order, S>[]>, NarrowedEntity<Order, S>[]>(...args);
// #endregion

@customElement('ils-evaluation-overview')
export class ILSEvaluationOverview extends ChemistryLitElement<ILSThemeDefinition> {
  override styles() {
    return [
      {
        ':host': {
          padding: this.spacing.lg,
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    ];
  }

  @state() private readonly _examDay = PRXController<typeof extendedExamDaySelector>(
    this,
    routingState.searchParams.pipe(
      tap(() => {
        this._orders.isLoading = true;
        this._ratedTrainees.isLoading = true;
        this._slots.isLoading = true;
      }),
      switchMap(params => {
        const day = params.get('day');
        if (day) return subscribeByID(ExamDay, day)(extendedExamDaySelector);
        return of(null);
      }),
    ),
  );

  @state() private readonly _slots = new RXController(
    this,
    this._examDay.observable.pipe(
      switchMap(examDay => (examDay ? subscribeSlotsForEvaluation(examDay.slotIDs)(slotSelector) : of([] as Slot[]))),
    ),
  );

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());

  @state() private readonly _inspectors = new RXController(
    this,
    ILSUser.getUsersByRoles([UserRole.INSPECTOR])({
      _id: 1,
      _class: 1,
      username: 1,
      specialization: 1,
      ecommerce: { _class: 1, firstName: 1, lastName: 1 },
    }),
  );

  @state() private readonly _ratedTrainees = URXController<typeof traineeSelector>(
    this,
    this._slots.observable.pipe(
      filter(Boolean),
      switchMap(slots => {
        return slots?.length
          ? subscribeByID(
              ILSUser,
              slots.map(s => s.candidateID),
            )(traineeSelector)
          : of([]);
      }),
    ),
  );

  @state() private readonly _orders = ORXController<typeof orderSelector>(
    this,
    this._ratedTrainees.observable.pipe(
      filter(Boolean),
      withLatestFrom(this._examDay.observable.pipe(filter(Boolean))),
      switchMap(([trainees, examDay]) => {
        const traineeIDs = trainees.map(trainee => trainee._id);
        if (!examDay?._id) return of([]);

        return getOrdersByExamDayAndTrainees(examDay._id, traineeIDs)(orderSelector);
      }),
    ),
  );

  @state() private readonly _scores = new RXController(
    this,
    this._ratedTrainees.observable.pipe(
      filter(Boolean),
      withLatestFrom(this._examDay.observable.pipe(filter(Boolean))),
      switchMap(([trainees, examDay]) => {
        const traineeIDs = trainees.map(trainee => trainee._id);
        if (!examDay?._id) return of([] as NarrowedEntity<Score, typeof scoreSelector>[]);

        return subscribeScoresByExamDayAndCandidates(traineeIDs, examDay._id)(scoreSelector);
      }),
    ),
  );

  @state() private readonly _evalAppointments = new RXController(
    this,
    this._slots.observable.pipe(
      filter(Boolean),
      switchMap(slots => subscribeTerminsBySlotIDs(slots.map(slot => slot._id))(terminSelector)),
    ),
  );

  private get _allRated() {
    return (
      this._scores.value?.length &&
      this._scores.value.every(score => score.status === 'succeeded' || score.status === 'failed')
    );
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this._currentUser.observable
      .pipe(
        filter(Boolean),
        take(1),
        map(user => isSidebarOpen.next(false)),
        takeUntil(this.disconnected),
      )
      .subscribe();
  }

  override render() {
    if (
      this._slots.isLoading ||
      this._orders.isLoading ||
      this._currentUser.isLoading ||
      this._ratedTrainees.isLoading ||
      this._evalAppointments.isLoading
    )
      return html`
        <x-flex center crossaxis-center space="md" ${css({ width: '100%', height: '100%' })}>
          <ils-loader></ils-loader>
          <x-text>Das Laden kann aktuell kurz dauern</x-text>
        </x-flex>
      `;

    if (!this._examDay.value || !this._slots.value || !this._ratedTrainees.value || !this._orders.value)
      return html`
        <x-flex center crossaxis-center space="md" ${css({ width: '100%', height: '100%' })}>
          <x-text>Bitte wähle einen Termin über die Sidebar aus.</x-text>
        </x-flex>
      `;

    return html`
      <x-flex padding="md" space="md" ${css({ height: '100%' })}>
        <x-flex horizontal crossaxis-center space="md">
          ${permissionGuard({
            resource: 'results',
            action: 'sendmail',
            guardedTemplate: html`
              <x-flex>
                <x-button
                  ?disabled=${!this._allRated}
                  @click=${async (e: CustomEvent) => {
                    if (!this._ratedTrainees.value) return;
                    const button = e.currentTarget as XButton;
                    button.loading = true;
                    try {
                      const confirm = await XDialog.confirm(
                        'Sind sie sicher, dass die alle Ergebnisse rauschicken wollen?',
                      ).catch(e => logger.error(e));
                      if (confirm) {
                        const sendResult = await ExamDay.sendRatingResultMails(
                          this._examDay.value!._id,
                          this._ratedTrainees.value.map(trainee => trainee._id),
                        )();
                        if (!sendResult)
                          await XDialog.alert(
                            'Bei einem oder mehreren Trainees konnte die Ergebnismail nicht versendet werden.',
                          );
                      }
                    } catch (error: any) {
                      logger.error(e);
                      void XDialog.alert(error.message);
                    }
                    button.loading = false;
                  }}
                >
                  Ergebnisse verschicken
                </x-button>
                ${this._allRated
                  ? nothing
                  : html`<x-tooltip><x-text>Es wurden noch nicht alle Prüflinge bewertet.</x-text></x-tooltip>`}
              </x-flex>
            `,
          })}
          ${['development', 'qa'].includes(baseConfig.get('DEPLOYMENT'))
            ? permissionGuard({
                resource: 'termins',
                action: 'schedule',
                guardedTemplate: html` <x-button @click=${() => runTerminScheduler()()}> Run Scheduler </x-button> `,
              })
            : nothing}
        </x-flex>
        ${this._slots.value.length
          ? html`
              <x-flex flex>
                <x-grid padding="md" space="md" columns="max-content 40px 1fr repeat(4, max-content)">
                  ${this.header()}
                  ${repeat(
                    this._ratedTrainees.value,
                    trainee => trainee._id,
                    (trainee, idx) => this.userBlock(trainee, idx),
                  )}
                </x-grid>
              </x-flex>
            `
          : html`
              <x-flex ${css({ width: '100%', height: '100%' })} center crossaxis-center space="md">
                <x-h2> Dieser Tag wurde noch nicht geplant </x-h2>
              </x-flex>
            `}
      </x-flex>
    `;
  }

  private userBlock(user: NarrowedEntity<ILSUser, typeof traineeSelector>, idx: number) {
    const examDay = this._examDay.value;
    if (!examDay) return nothing;
    if (!this._slots.value) return nothing;
    const userSlots = this._slots.value
      .filter(slot => slot.candidateID === user._id)
      .filter(slot => !slot.fallbackSlotID);
    if (!userSlots.length) return nothing;

    const slotGroups = getGroupsFromItemsAndTesterTuples(userSlots, examDay.osceTesterTuples);
    const userOrder = this._orders.value?.find(order => order.userReference.userID === user._id);
    const userScore = this._scores.value?.find(order => order.candidateID === user._id);

    const isRatingComplete = !!userScore && (userScore.status === 'succeeded' || userScore.status === 'failed');
    const isPassed = !!userScore && userScore.status === 'succeeded';
    const resultMailAlreadySent = userOrder ? user.resultMailSent(userOrder._id) : false;
    const certMailAlreadySent = userOrder ? user.certificateMailSent(userOrder._id) : false;

    return html`
      ${idx !== 0
        ? html`
            <div
              ${css({
                height: '1px',
                borderBottom: `1px solid ${this.colors.neutral.shade(200)}`,
                gridColumn: '1 / -1',
              })}
            ></div>
          `
        : nothing}

      <x-flex center crossaxis-center space="sm" ${css({ gridRow: `span ${userSlots.length + slotGroups.length}` })}>
        <x-flex horizontal space="sm">
          <x-text ${css({ minWidth: '70px' })}>
            ${user.ecommerce?.fullName ?? user.username}
            ${userScore?.isRatingComplete() ? `(${((userScore.getTotalScore() ?? 0) * 100).toFixed(2)}%)` : ''}
          </x-text>
        </x-flex>
        <x-flex horizontal space="sm">
          ${this.sendResultsButton(userOrder, isRatingComplete, resultMailAlreadySent, user)}
          ${this.sendCertificatesButton(userOrder, isPassed, isRatingComplete, certMailAlreadySent, user)}
        </x-flex>
      </x-flex>
      ${repeat(slotGroups, slotGroup => {
        return html`
          ${this._slotGroup(slotGroup, userScore)}
          ${repeat(
            slotGroup.items,
            slot => slot._id,
            slot =>
              this.slotLine(
                slot,
                userScore?.slotScores.find(score => score.slotID === slot._id),
              ),
          )}
        `;
      })}
    `;
  }

  private sendCertificatesButton(
    userOrder: NarrowedEntity<Order, typeof orderSelector> | undefined,
    isPassed: boolean,
    isRatingComplete: boolean,
    mailAlreadySent: boolean,
    user: NarrowedEntity<ILSUser, typeof traineeSelector>,
  ): Renderable {
    if (!userOrder) return nothing;
    if (!isPassed) return nothing;

    // TODO merge this logic with the send certificate button in ils-admin-order-detail
    return permissionGuard({
      resource: 'certificates',
      action: 'sendmail',
      guardedTemplate: html`
        <x-icon
          @click=${async () => {
            try {
              if (!isRatingComplete || mailAlreadySent) return;
              const confirm = await XDialog.confirm(
                `Sind Sie sicher, dass Sie eine E-Mail mit dem Zertifikat an den Teilnehmer '${
                  user.ecommerce?.fullName ?? ''
                }' versenden wollen?`,
              );
              if (!confirm) return;
              const successful = await XDialog.waitFor(
                ExamDay.sendCertificateMail(userOrder._id)()
                  .then(() => true)
                  .catch(e => XDialog.alert('Es gab ein Problem beim Versenden der Mail: ' + (e as Error).message)),
              );
              if (successful) void XSnackbar.show('Zertifikat erfolgreich an Prüfling gesendet');
            } catch (e) {
              logger.error(e);
              void XDialog.alert('Es gab einen Fehler beim Versenden der Mail: ' + (e as Error).message);
            }
          }}
          ${css({
            color: this.getSendButtonColor(mailAlreadySent, isRatingComplete),
            cursor: isRatingComplete && !mailAlreadySent ? 'pointer' : 'default',
            userSelect: 'none',
          })}
        >
          school
          <x-tooltip
            .text=${html`
              <x-text>
                ${mailAlreadySent
                  ? 'Zertifikat wurde erfolgreich an Teilnehmer versandt'
                  : 'Zertifikat an Teilnehmer verschicken'}
              </x-text>
            `}
          >
          </x-tooltip>
        </x-icon>
      `,
    });
  }

  private sendResultsButton(
    userOrder: NarrowedEntity<Order, typeof orderSelector> | undefined,
    isRatingComplete: boolean,
    mailAlreadySent: boolean,
    user: NarrowedEntity<ILSUser, typeof traineeSelector>,
  ): unknown {
    // TODO merge this logic with the send results button in ils-admin-order-detail
    if (!userOrder)
      return html`
        <x-text tone="warning" ${css({ maxWidth: '250px', textAlign: 'center' })}>
          Keine aktive Bestellung für diesen Nutzer an diesem Prüfungstag gefunden
        </x-text>
      `;
    return permissionGuard({
      resource: 'results',
      action: 'sendmail',
      guardedTemplate: html`
        <x-icon
          @click=${async () => {
            try {
              if (!isRatingComplete || mailAlreadySent) return;
              const confirm = await XDialog.confirm(
                `Sind Sie sicher, dass Sie eine E-Mail mit den Ergebnissen der Bewertung an den Teilnehmer '${
                  user.ecommerce?.fullName ?? ''
                }' versenden wollen?`,
              );
              if (!confirm) return;

              const successful = await XDialog.waitFor(
                ExamDay.sendRatingResultMail(userOrder._id)()
                  .then(() => {
                    return true;
                  })
                  .catch(e => XDialog.alert('Es gab ein Problem beim Versenden der Mail: ' + (e as Error).message)),
              );
              if (successful) {
                void XSnackbar.show('Ergebnis erfolgreich an Prüfling gesendet');
              }
            } catch (e) {
              logger.error(e);
              void XDialog.alert('Es gab einen Fehler beim Versenden der Mail: ' + (e as Error).message);
            }
          }}
          ${css({
            color: this.getSendButtonColor(mailAlreadySent, isRatingComplete),
            cursor: isRatingComplete && !mailAlreadySent ? 'pointer' : 'default',
            userSelect: 'none',
          })}
        >
          fact_check
          <x-tooltip
            .text=${html`
              <x-text>
                ${mailAlreadySent
                  ? 'Ergebnis wurde erfolgreich an Teilnehmer versandt'
                  : 'Ergebnis an Teilnehmer verschicken'}
              </x-text>
            `}
          >
          </x-tooltip>
        </x-icon>
      `,
    });
  }

  private getSendButtonColor(alreadySent: boolean, ratingComplete: boolean) {
    if (alreadySent) return this.colors.green;
    if (ratingComplete) return this.colors.accent;
    return this.colors.neutral.shade(400);
  }

  private slotLine(slot: Slot, slotScore?: NarrowedEntity<SlotScore, typeof scoreSelector.slotScores>) {
    const ratingComplete = !!slotScore && slotScore.score !== -1;
    return html`
      <x-flex center crossaxis-center>
        <ils-osce-type .osce=${slot.osce}></ils-osce-type>
      </x-flex>
      <x-flex horizontal crossaxis-center space="sm" ${css({ paddingRight: this.spacing.md })}>
        <x-text> ${slot.osce?.titleTrainee} </x-text> ${permissionGuard({
          resource: 'users',
          action: 'impersonate',
          guardedTemplate: html`
            <x-button
              mode="text"
              tone="subtle"
              icon="content_copy"
              @click=${async () => {
                await navigator.clipboard.writeText(slot._id);
                void XSnackbar.show('SlotID kopiert');
              }}
            >
              <x-tooltip> <x-text> &lt;DEV&gt; Kopiere SlotID ${slot._id} </x-text> </x-tooltip>
            </x-button>
          `,
        })}
      </x-flex>
      ${permissionGuard({
        resource: 'ratings',
        action: 'viewothers',
        guardedTemplate: html`
          <x-flex horizontal crossaxis-center space="sm" end>
            ${this.inspector(Specialization.DOCTOR, slot, slotScore)}
          </x-flex>
          <x-flex horizontal crossaxis-center space="sm" end>
            ${this.inspector(Specialization.LINGUIST, slot, slotScore)}
          </x-flex>
          <x-flex horizontal crossaxis-center space="xs" end>
            ${this.ratingDate(slot, ratingComplete, slotScore)}
          </x-flex>
        `,
        forbiddenTemplate: html` <div ${css({ gridColumn: 'span 3' })}></div> `,
      })}
      ${this._slotLineInspectButton(slot, ratingComplete, slotScore)}
    `;
  }

  private _slotLineInspectButton(
    slot: Slot,
    ratingComplete: boolean,
    slotScore?: NarrowedEntity<SlotScore, typeof scoreSelector.slotScores>,
  ) {
    const canViewOthers = hasPermission(this._currentUser.value, 'ratings', 'viewothers');
    const answerValid = slot.answer?.isValid();
    const answerHasError = !!slot.answer?.reportedError;
    const icon = slot.isCandidateMissing
      ? 'person_off'
      : answerValid
      ? ratingComplete
        ? 'check'
        : canViewOthers
        ? 'search'
        : ''
      : answerHasError
      ? 'error'
      : 'close';
    const style: Styles = { alignSelf: 'end' };
    if (!answerValid) style.color = answerHasError ? this.colors.tone.error : this.colors.neutral.shade(600);
    const text = slot.isCandidateMissing
      ? 'Kandidat als fehlend gemeldet'
      : slot.answer
      ? answerValid
        ? ratingComplete
          ? `Bewertet (${(slotScore!.score * 100).toFixed(2)}%)`
          : canViewOthers
          ? 'Ansehen'
          : ''
        : answerHasError
        ? 'Fehler gemeldet'
        : slot.answer.type === 'voice'
        ? 'Audiospur fehlt'
        : 'leere Antwort'
      : 'noch keine Antwort';
    return canViewOthers
      ? html`
          <x-flex horizontal crossaxis-center space="sm" end>
            <x-button
              mode="text"
              icon=${icon}
              ${css(style)}
              @click=${() => {
                return ILSEvaluationInspectDialog.showPopup({
                  props: { slotID: slot._id, inspectors: this._inspectors.value },
                });
              }}
            >
              ${text}
            </x-button>
          </x-flex>
        `
      : html`
          <x-flex horizontal crossaxis-center space="sm" end ${css(style)}>
            <x-icon> ${icon} </x-icon>
            <x-text> ${text} </x-text>
          </x-flex>
        `;
  }

  private _slotGroup(
    slotGroup: { osceGroupID?: string; items: Slot[] },
    userScore?: NarrowedEntity<Score, typeof scoreSelector>,
  ) {
    if (!slotGroup.osceGroupID) return html` <div ${css({ gridColumn: '2 / -1' })}></div>`;

    return html`
      <x-flex center ${css({ gridColumn: '2 / -1', marginTop: this.spacing.sm })}>
        <x-text bold>
          OSCE-Gruppe
          ${userScore?.isOSCEGroupRated(slotGroup.osceGroupID)
            ? `(${(userScore.getOSCEGroupScore(slotGroup.osceGroupID) * 100).toFixed(2)}%)`
            : ''}
        </x-text>
      </x-flex>
    `;
  }

  private header() {
    return html`
      <x-h4> Prüfling </x-h4>
      <x-h4 ${css({ gridColumn: 'span 2' })}> Titel </x-h4>
      ${permissionGuard({
        resource: 'ratings',
        action: 'viewothers',
        guardedTemplate: html`
          <x-h4> ${Specialization.DOCTOR} </x-h4>
          <x-h4> ${Specialization.LINGUIST} </x-h4>
          <x-h4> Bewertet am </x-h4>
        `,
        forbiddenTemplate: html` <div ${css({ gridColumn: 'span 3' })}></div> `,
      })}
      <div></div>
    `;
  }

  private ratingDate(
    slot: Slot,
    ratingComplete: boolean,
    slotScore?: NarrowedEntity<SlotScore, typeof scoreSelector.slotScores>,
  ) {
    if (ratingComplete)
      return html`
        <x-text>
          ${slotScore?.updatedAt
            ? DateTime.fromJSDate(slotScore.updatedAt).toLocaleString(DateTime.DATETIME_SHORT)
            : '-'}
        </x-text>
      `;

    const evalAppointment = this._evalAppointments.value?.find(appointment => appointment.slotIDs.includes(slot._id));
    if (evalAppointment) {
      const appointmentOver = evalAppointment.end < DateTime.now();
      const interval = evalAppointment.start.until(evalAppointment.end) as Interval<true>;
      if (appointmentOver)
        return html`
          <x-icon ${css({ color: this.colors.tone.error })}> event_busy </x-icon>
          <x-tooltip>
            <x-text>
              Synchroner Bewertungstermin am ${interval.toLocaleString(DateTime.DATETIME_SHORT)}. Situation war nach
              Ende des Termins nicht bewertet. Sie wird in der nächsten Planungsrunde neu geplant.
            </x-text>
          </x-tooltip>
          <x-text tone="error"> ${evalAppointment.start.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
        `;
      return html`
        <x-icon ${css({ color: this.colors.tone.subtle })}> event </x-icon>
        <x-tooltip>
          <x-text> Synchroner Bewertungstermin am ${interval.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
        </x-tooltip>
        <x-text tone="subtle"> ${evalAppointment.start.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
      `;
    }

    return html` <x-text tone="subtle"> Ausstehend </x-text> `;
  }

  private inspector(
    specialization: Specialization,
    slot: Slot,
    slotScore?: NarrowedEntity<SlotScore, typeof scoreSelector.slotScores>,
  ) {
    const syncRatingComplete = !!slotScore && slotScore.score !== -1;
    const finalRating = slot.ratings.find(r => r._class === Names.ExamSlotFinalRatingDoc._class);
    if (syncRatingComplete && !finalRating)
      return html`
        <x-text tone="subtle"> - </x-text> <x-tooltip> <x-text> Bewertung übersprungen </x-text> </x-tooltip>
      `;
    const inspector = this._inspectors.value?.find(
      i => i.specialization === specialization && slot.ratings.map(r => r.inspector).includes(i._id),
    );
    const asyncRating = slot.ratings.find(r => r.inspector === inspector?._id && r.archived !== true);
    if (!asyncRating || !inspector) return html` <x-text tone="subtle"> Ausstehend </x-text> `;
    const inspectorName = inspector.ecommerce?.fullName ?? inspector.username;
    if (!syncRatingComplete && asyncRating.committed)
      return html`
        <x-text tone="subtle"> ${inspectorName} </x-text>
        <x-tooltip>
          <x-text> Asynchron bewertet am ${asyncRating.updatedAt?.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
        </x-tooltip>
      `;
    if (!syncRatingComplete && !asyncRating.committed)
      return html`
        <x-text tone="subtle"> ${inspectorName} </x-text>
        <x-tooltip>
          <x-text>
            In Asynchroner Bewertung. Letztes Update am
            ${asyncRating.updatedAt?.toLocaleString(DateTime.DATETIME_SHORT)}
          </x-text>
        </x-tooltip>
      `;
    if (syncRatingComplete && finalRating?.committed)
      return html`
        <x-text> ${inspectorName} </x-text>
        <x-tooltip>
          <x-text> Bewertet am ${finalRating.updatedAt?.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
        </x-tooltip>
      `;
    if (syncRatingComplete && finalRating && !finalRating.committed)
      return html`
        <x-text> ${inspectorName} </x-text>
        <x-tooltip>
          <x-text>
            In Synchroner Bewertung. Letztes Update am ${finalRating.updatedAt?.toLocaleString(DateTime.DATETIME_SHORT)}
          </x-text>
        </x-tooltip>
      `;
    return html` <x-text> tbd </x-text> `;
  }
}
